import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const CookiePolicy = () => {
  const [cookiePreferences, setCookiePreferences] = useState({
    essential: true, // Always required
    functional: true,
    analytics: false,
    marketing: false,
    personalization: false
  });

  const cookieData = {
    lastUpdated: '23 Septembre 2025',
    categories: [
      {
        id: 'essential',
        name: 'Cookies Essentiels',
        required: true,
        description: 'Nécessaires au fonctionnement de base de la plateforme',
        examples: [
          'Session utilisateur et authentification',
          'Panier de propriétés sauvegardées',
          'Préférences de langue et région',
          'Sécurité et prévention des fraudes'
        ],
        retention: 'Session ou 30 jours maximum'
      },
      {
        id: 'functional',
        name: 'Cookies Fonctionnels',
        required: false,
        description: 'Améliorent l\'expérience utilisateur avec des fonctionnalités supplémentaires',
        examples: [
          'Géolocalisation pour recherches locales',
          'Historique des propriétés consultées',
          'Paramètres de filtres personnalisés',
          'Chat en direct avec les notaires'
        ],
        retention: 'Jusqu\'à 12 mois'
      },
      {
        id: 'analytics',
        name: 'Cookies d\'Analyse',
        required: false,
        description: 'Collectent des statistiques anonymisées pour améliorer nos services',
        examples: [
          'Pages les plus visitées',
          'Temps passé sur chaque section',
          'Parcours utilisateur anonymisé',
          'Performance des vidéos immersives'
        ],
        retention: 'Jusqu\'à 26 mois (Google Analytics)'
      },
      {
        id: 'marketing',
        name: 'Cookies Marketing',
        required: false,
        description: 'Permettent la personnalisation publicitaire et le remarketing',
        examples: [
          'Publicités ciblées selon vos intérêts',
          'Remarketing des propriétés consultées',
          'Mesure d\'efficacité des campagnes',
          'Intégration réseaux sociaux'
        ],
        retention: 'Jusqu\'à 24 mois'
      },
      {
        id: 'personalization',
        name: 'Cookies de Personnalisation',
        required: false,
        description: 'Adaptent le contenu à vos préférences et comportements',
        examples: [
          'Recommandations de propriétés personnalisées',
          'Interface adaptée à vos habitudes',
          'Notifications personnalisées',
          'Contenu basé sur votre localisation'
        ],
        retention: 'Jusqu\'à 18 mois'
      }
    ]
  };

  const handlePreferenceChange = (category, value) => {
    if (category === 'essential') return; // Cannot disable essential cookies
    
    setCookiePreferences(prev => ({
      ...prev,
      [category]: value
    }));
  };

  const handleSavePreferences = () => {
    // Save preferences to localStorage and apply settings
    localStorage.setItem('cookiePreferences', JSON.stringify(cookiePreferences));
    
    // Apply preferences (this would integrate with your analytics/marketing tools)
    console.log('Cookie preferences saved:', cookiePreferences);
    
    // Show success message or redirect
    alert('Vos préférences de cookies ont été sauvegardées.');
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      essential: true,
      functional: true,
      analytics: true,
      marketing: true,
      personalization: true
    };
    setCookiePreferences(allAccepted);
    localStorage.setItem('cookiePreferences', JSON.stringify(allAccepted));
    alert('Tous les cookies ont été acceptés.');
  };

  const handleRejectOptional = () => {
    const onlyEssential = {
      essential: true,
      functional: false,
      analytics: false,
      marketing: false,
      personalization: false
    };
    setCookiePreferences(onlyEssential);
    localStorage.setItem('cookiePreferences', JSON.stringify(onlyEssential));
    alert('Seuls les cookies essentiels ont été conservés.');
  };

  return (
    <div className="p-8">
      <div className="flex items-center space-x-4 mb-8">
        <div className="w-12 h-12 bg-brand-gold/10 rounded-xl flex items-center justify-center">
          <Icon name="Cookie" size={24} color="var(--color-brand-earth)" />
        </div>
        <div>
          <h2 className="font-heading font-bold text-2xl text-brand-earth">
            Politique des Cookies
          </h2>
          <p className="text-gray-600 text-sm">
            Dernière mise à jour: {cookieData?.lastUpdated}
          </p>
        </div>
      </div>
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-8">
        <div className="flex items-start space-x-3">
          <Icon name="Cookie" size={20} color="var(--color-orange-600)" />
          <div>
            <p className="text-orange-800 text-sm font-medium mb-2">
              <strong>Qu'est-ce qu'un cookie ?</strong>
            </p>
            <p className="text-orange-700 text-sm">
              Un cookie est un petit fichier stocké sur votre appareil lors de votre visite sur notre site. 
              Il nous aide à améliorer votre expérience et à vous fournir des services personnalisés.
            </p>
          </div>
        </div>
      </div>
      {/* Cookie Categories */}
      <div className="space-y-6 mb-8">
        {cookieData?.categories?.map((category) => (
          <div key={category?.id} className="bg-white border rounded-xl p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h3 className="font-heading font-semibold text-lg text-brand-earth">
                    {category?.name}
                  </h3>
                  {category?.required && (
                    <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full">
                      Requis
                    </span>
                  )}
                </div>
                <p className="text-gray-700 text-sm mb-4">{category?.description}</p>
              </div>
              
              <div className="flex-shrink-0 ml-4">
                <Checkbox
                  id={`cookie-${category?.id}`}
                  checked={cookiePreferences?.[category?.id]}
                  onChange={(checked) => handlePreferenceChange(category?.id, checked)}
                  disabled={category?.required}
                  label={category?.required ? 'Toujours Actif' : 'Autoriser'}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-body font-semibold text-gray-800 mb-2">Exemples d'utilisation:</h4>
                <ul className="text-gray-600 text-sm space-y-1">
                  {category?.examples?.map((example, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <Icon name="Check" size={14} color="var(--color-brand-gold)" className="mt-0.5 flex-shrink-0" />
                      <span>{example}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div>
                <h4 className="font-body font-semibold text-gray-800 mb-2">Durée de conservation:</h4>
                <div className="flex items-center space-x-2 text-gray-600 text-sm">
                  <Icon name="Clock" size={14} color="var(--color-brand-earth)" />
                  <span>{category?.retention}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Preference Management */}
      <div className="bg-brand-earth/5 rounded-xl p-6 mb-8">
        <h3 className="font-heading font-semibold text-brand-earth mb-4">
          Gestion de vos Préférences
        </h3>
        <p className="text-gray-700 text-sm mb-6">
          Vous pouvez modifier vos préférences de cookies à tout moment. Les cookies essentiels 
          ne peuvent pas être désactivés car ils sont nécessaires au fonctionnement du site.
        </p>
        
        <div className="flex flex-col md:flex-row space-y-3 md:space-y-0 md:space-x-4">
          <Button
            onClick={handleAcceptAll}
            variant="primary"
            iconName="CheckCircle"
            iconPosition="left"
          >
            Accepter Tous les Cookies
          </Button>
          
          <Button
            onClick={handleSavePreferences}
            variant="secondary"
            iconName="Save"
            iconPosition="left"
          >
            Sauvegarder mes Préférences
          </Button>
          
          <Button
            onClick={handleRejectOptional}
            variant="outline"
            iconName="X"
            iconPosition="left"
          >
            Refuser les Cookies Optionnels
          </Button>
        </div>
      </div>
      {/* Third Party Services */}
      <div className="bg-white border rounded-xl p-6 mb-8">
        <h3 className="font-heading font-semibold text-brand-earth mb-4 flex items-center space-x-2">
          <Icon name="ExternalLink" size={20} color="var(--color-brand-earth)" />
          <span>Services Tiers et Cookies</span>
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-body font-semibold text-gray-800 mb-3">Services Intégrés:</h4>
            <div className="space-y-2 text-sm text-gray-700">
              <div className="flex items-center space-x-2">
                <Icon name="BarChart3" size={14} color="var(--color-brand-earth)" />
                <span>Google Analytics (analyse du trafic)</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="MapPin" size={14} color="var(--color-brand-earth)" />
                <span>Google Maps (géolocalisation)</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Video" size={14} color="var(--color-brand-earth)" />
                <span>Vimeo/YouTube (vidéos immersives)</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="MessageCircle" size={14} color="var(--color-brand-earth)" />
                <span>Intercom (chat client)</span>
              </div>
            </div>
          </div>
          
          <div>
            <h4 className="font-body font-semibold text-gray-800 mb-3">Liens de Désinscription:</h4>
            <div className="space-y-2 text-sm">
              <a href="https://tools.google.com/dlpage/gaoptout" className="flex items-center space-x-2 text-brand-earth hover:text-brand-gold">
                <Icon name="ExternalLink" size={14} />
                <span>Désactiver Google Analytics</span>
              </a>
              <a href="https://www.facebook.com/help/568137493302217" className="flex items-center space-x-2 text-brand-earth hover:text-brand-gold">
                <Icon name="ExternalLink" size={14} />
                <span>Paramètres Publicitaires Facebook</span>
              </a>
              <a href="https://support.google.com/accounts/answer/61416" className="flex items-center space-x-2 text-brand-earth hover:text-brand-gold">
                <Icon name="ExternalLink" size={14} />
                <span>Gérer les Cookies Google</span>
              </a>
            </div>
          </div>
        </div>
      </div>
      {/* Help Section */}
      <div className="p-6 bg-brand-earth/5 rounded-xl">
        <h3 className="font-heading font-semibold text-brand-earth mb-3">
          Besoin d'Aide avec les Cookies ?
        </h3>
        <p className="text-gray-700 text-sm mb-4">
          Notre équipe support est disponible pour vous aider à configurer vos préférences de cookies.
        </p>
        <div className="flex flex-col md:flex-row md:items-center space-y-2 md:space-y-0 md:space-x-6 text-sm">
          <div className="flex items-center space-x-2">
            <Icon name="Mail" size={16} color="var(--color-brand-earth)" />
            <span>cookies@kapitalstates.com</span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="MessageCircle" size={16} color="var(--color-brand-earth)" />
            <span>Chat en direct disponible 24/7</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicy;